import React, { useEffect } from 'react';
import CheckIcon from '@assets/icons/admin-center/check.svg';
import CloseIcon from '@assets/icons/admin-center/close.svg';
import BaseProgress from '@components/progress';
import {
    CompletedWrapper,
    FileInfo,
    Filename,
    StopUploadingWrapper,
    UploadInfoWrapper,
    UploadProgressBarWrapper,
    UploadProgressWrapper,
    VisualProgressBarWrapper,
} from '@components/upload-progress-bar/styled.tsx';
import { TEST_IDS } from '@constants/testIds.ts';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { translateItem } from '@utils/translations.ts';

export const SHARED_UPLOAD_PROGRESS_BAR_STOP_UPLOADING = msg`Stop Uploading`;
export const SHARED_UPLOAD_PROGRESS_BAR_MIN_LEFT = msg`min left`;

interface Props {
    filename: string;
    sizeInMB: number;
    timeInSeconds: number;
    percentageProgress: number;
    minutesLeft: number;
    isOpened: boolean;
    onStopUploading: () => void;
    onCompleted: () => void;
}

const IN_PROGRESS_COLOR = '#2171C6';
const TRAIL_COLOR = '#CEE6FF';
const COMPLETE_COLOR = '#22a06b';

export const UploadProgressBar: React.FC<Props> = ({
    filename,
    sizeInMB,
    timeInSeconds,
    percentageProgress,
    minutesLeft,
    isOpened,
    onStopUploading,
    onCompleted,
}) => {
    const { t } = useLingui();

    const isUploadCompleted = percentageProgress === 100;
    const strokeColor = isUploadCompleted ? COMPLETE_COLOR : IN_PROGRESS_COLOR;

    const handleStopUploading = () => {
        if (isUploadCompleted) return;
        onStopUploading();
    };

    useEffect(() => {
        if (!isUploadCompleted) return;

        const timeoutId = setTimeout(onCompleted, 2000);

        return () => clearTimeout(timeoutId);
    }, [isUploadCompleted, onCompleted]);

    return isOpened ? (
        <UploadProgressBarWrapper data-cy={TEST_IDS.SHARED.UPLOAD_PROGRESS_BAR}>
            <UploadInfoWrapper>
                <div>
                    <Filename>{filename}</Filename>{' '}
                    <FileInfo>
                        ({t`Size`}: {sizeInMB} MB
                        {timeInSeconds > 0 && `, ${t`Time`}: ${timeInSeconds}s`})
                    </FileInfo>
                </div>
                <VisualProgressBarWrapper>
                    <BaseProgress
                        percent={percentageProgress}
                        showInfo={false}
                        strokeColor={strokeColor}
                        railColor={TRAIL_COLOR}
                    />
                </VisualProgressBarWrapper>
                <UploadProgressWrapper>
                    {isUploadCompleted ? (
                        <CompletedWrapper>
                            <CheckIcon />
                            <div>{percentageProgress}%</div>
                        </CompletedWrapper>
                    ) : (
                        <div>{percentageProgress}%</div>
                    )}
                    {!isUploadCompleted && (
                        <div>
                            {minutesLeft} {translateItem(SHARED_UPLOAD_PROGRESS_BAR_MIN_LEFT)}
                        </div>
                    )}
                </UploadProgressWrapper>
            </UploadInfoWrapper>
            <StopUploadingWrapper $isUploadCompleted={isUploadCompleted}>
                <div onClick={handleStopUploading}>{translateItem(SHARED_UPLOAD_PROGRESS_BAR_STOP_UPLOADING)}</div>
                <CloseIcon />
            </StopUploadingWrapper>
        </UploadProgressBarWrapper>
    ) : null;
};
